import { Component, Input, OnInit, Output, OnChanges, SimpleChanges, EventEmitter, inject } from '@angular/core';
import { Product, Brand, ProductListDto, DisplayCategory, UmmaPick } from '../../../../dto';
import { PaginatedResponse, BaseComponent, OrderByValue } from '../../../../core';
import { Dialog } from 'primeng/dialog';
import { FormGroup, FormBuilder } from '@angular/forms';
import { CERTIFICATION_TYPE, CodeService, CommonCode } from '../../../../shared';
import { TPipe } from '../../../../common/pipes/t.pipe';
import { Utils, Tree } from '../../../../common';
import { UmmaPickService } from '../../../../services';

@Component({
  selector: 'app-filtered-product-list',
  templateUrl: './filtered-product-list.component.html',
  styleUrls: ['./filtered-product-list.component.scss'],
  standalone: false,
})
export class FilteredProductListComponent extends BaseComponent implements OnInit, OnChanges {
  private formBuilder = inject(FormBuilder);
  private codeService = inject(CodeService);
  private t = inject(TPipe);
  private ummaPickService = inject(UmmaPickService);

  SEARCH_TYPE = Object.freeze({
    SEARCH: 'SEARCH',
    BRAND: 'BRAND',
    CATEGORY: 'CATEGORY',
  });

  FILTER_SECTION = Object.freeze({
    SORT: 'Sort',
    CATEGORY: 'Category',
    BRAND: 'Brand',
    PRICE_RANGE: 'Price Range',
    MOQ: 'MOQ',
    MOA: 'MOA',
    CERTIFICATION: 'Certification',
  });

  PRICE_TYPE = Object.freeze({
    ALL: 'TERM.ALL',
    PRICE: 'TERM.PRICE',
    SUPPLY_RATE: 'TERM.SUPPLY_RATE',
  });

  ORDER_BY = Object.freeze([
    { key: 'FILTERED_PRODUCT_LIST.SORT.MATCH_SCORE', value: 'orderByMatchScore', orderBy: OrderByValue.DESC },
    { key: 'FILTERED_PRODUCT_LIST.SORT.CREATED', value: 'orderById', orderBy: OrderByValue.DESC },
    {
      key: 'FILTERED_PRODUCT_LIST.SORT.SELLING_LOWER',
      value: 'orderByBestSelling',
      orderBy: OrderByValue.DESC,
    },
    {
      key: 'FILTERED_PRODUCT_LIST.SORT.HIGHER',
      value: 'orderBySaleSupplyPrice',
      orderBy: OrderByValue.ASC,
    },
    {
      key: 'FILTERED_PRODUCT_LIST.SORT.LOWER',
      value: 'orderBySaleSupplyPrice',
      orderBy: OrderByValue.DESC,
    },
    // {
    //   key: 'FILTERED_PRODUCT_LIST.SORT.SELLING_HIGHER',
    //   value: 'orderByBestSelling',
    //   orderBy: OrderByValue.ASC,
    // },
  ]);

  @Input()
  pagenatedProducts: PaginatedResponse<Product>;

  @Input()
  filter: ProductListDto;

  @Input()
  allDisplayCategories: DisplayCategory[];

  @Input()
  allBrands: Brand[];

  @Input() filterType: string;

  @Output()
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  onChangeFilter = new EventEmitter<ProductListDto>();
  isCpnp = false;
  certificationTypes: CommonCode[];

  isSelectedAllBrand = true;
  isSelectedAllCategory = true;
  isShowFilterModal = false;
  isShowOptions = false;
  selectedFilterSection: string;
  isUsedFilter = false;
  ummaPickProducts: UmmaPick[] = [];
  ummaPickItemCount = [];

  /** 장바구니/GA 리스트 출처 (미지정 시 filterType 기준 fallback) */
  @Input() itemListName?: string;

  selected: any = {
    priceType: this.PRICE_TYPE.ALL,
    brands: [],
    displayCategoryTree: {} as Tree<DisplayCategory>,
  };

  filterForm: FormGroup;

  get resolvedItemListName(): string {
    if (this.itemListName) {
      return this.itemListName;
    }
    switch (this.filterType) {
      case this.SEARCH_TYPE.BRAND:
        return 'brand';
      case this.SEARCH_TYPE.CATEGORY:
        return 'category';
      default:
        return 'category';
    }
  }

  constructor() {
    super();
    this.certificationTypes = this.codeService.getCommonCodes('CERTIFICATION_TYPE');
    this.filterForm = this.formBuilder.group({
      orderBy: [],
      minPrice: [],
      maxPrice: [],
      minRate: [],
      maxRate: [],
      minMoq: [],
      maxMoq: [],
      minMoa: [],
      maxMoa: [],
      certificationTypes: [],
      isCpnp: [],
    });
  }

  ngOnInit(): void {
    this.setOrderBy();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.pagenatedProducts) {
      this.pagenatedProducts = changes.pagenatedProducts.currentValue;
      this.getUmmaPick();
    }

    if (changes.filter) {
      this.filter = changes.filter.currentValue;
      this.filterForm.patchValue({
        minPrice: this.filter.startSalePrice,
        maxPrice: this.filter.endSalePrice,
        minRate: this.filter.startSupplyRate,
        maxRate: this.filter.endSupplyRate,
        minMoq: this.filter.startMoq,
        maxMoq: this.filter.endMoq,
        minMoa: this.filter.startMoa,
        maxMoa: this.filter.endMoa,
        certificationTypes: [CERTIFICATION_TYPE.CRUELTY_FREE, CERTIFICATION_TYPE.VEGAN_CERTIFIED],
        isCpnp: this.filter.isCpnp,
      });

      this.checkFiltering();
      this.setOrderBy();

      if (this.filterType !== this.SEARCH_TYPE.CATEGORY) {
        this.isSelectedAllCategory = true;
        // tslint:disable-next-line: no-unused-expression
        this.allDisplayCategories && this.onUncheckedCategoryAll();
      }

      if (this.filterType !== this.SEARCH_TYPE.BRAND) {
        this.isSelectedAllBrand = true;
        this.onUncheckedBrandAll();
      }
    }

    if (changes.allBrands) {
      this.allBrands = this.selected.brands = changes.allBrands.currentValue;
    }

    if (changes.allDisplayCategories) {
      this.allDisplayCategories = changes.allDisplayCategories.currentValue;
      this.setDisplayCategory(changes.allDisplayCategories.currentValue);
    }

    for (let name in changes) {
      if (changes[name].firstChange) {
        this.onUncheckedCertificationTypeAll();
        break;
      }
    }
  }

  checkFiltering() {
    const startMoq = this.filter.startMoq;
    const endMoq = this.filter.endMoq;
    const startMoa = this.filter.startMoa;
    const endMoa = this.filter.endMoa;
    if (this.filterType !== this.SEARCH_TYPE.BRAND && this.filter.brandIds && this.filter.brandIds.length !== 0) {
      this.isUsedFilter = true;
      return;
    }

    if (
      this.filterType !== this.SEARCH_TYPE.CATEGORY &&
      this.filter.displayCategories &&
      this.filter.displayCategories.length !== 0
    ) {
      this.isUsedFilter = true;
      return;
    }

    if (this.selected.priceType !== this.PRICE_TYPE.ALL) {
      this.isUsedFilter = true;
      return;
    }

    if (startMoq || endMoq) {
      this.isUsedFilter = true;
      return;
    }

    if (startMoa || endMoa) {
      this.isUsedFilter = true;
      return;
    }
    if (this.filterType === this.SEARCH_TYPE.SEARCH) {
      if (!this.filter.orderByMatchScore) {
        this.isUsedFilter = true;
        return;
      }
    } else {
      if (!this.filter.orderById) {
        this.isUsedFilter = true;
        return;
      }
    }
    this.isUsedFilter = false;
  }

  setOrderBy(orderBy?: any) {
    if (orderBy) {
      this.isShowOptions = false;
      if (orderBy === this.filterForm.get('orderBy').value) {
        return;
      }
      this.filterForm.get('orderBy').setValue(orderBy);
      this.onSubmitFilter();
    } else {
      if (this.filterType === this.SEARCH_TYPE.SEARCH) {
        orderBy = this.ORDER_BY[0];
      } else {
        orderBy = this.ORDER_BY[1];
      }

      this.ORDER_BY.some((item) => {
        if (this.filter[item.value]) {
          orderBy = item;
          return true;
        }
        return false;
      });
      this.filterForm.get('orderBy').setValue(orderBy);
    }
  }

  resetForm() {
    if (this.filterType === this.SEARCH_TYPE.SEARCH) {
      this.filterForm.get('orderBy').setValue(this.ORDER_BY[0]);
    } else if (this.filterType === this.SEARCH_TYPE.CATEGORY) {
      this.filterForm.get('orderBy').setValue(this.ORDER_BY[1]);
    } else {
      this.filterForm.get('orderBy').setValue(this.ORDER_BY[2]);
    }
    this.filterForm.get('minPrice').setValue(null);
    this.filterForm.get('maxPrice').setValue(null);
    this.filterForm.get('minRate').setValue(null);
    this.filterForm.get('maxRate').setValue(null);
    this.filterForm.get('minMoq').setValue(null);
    this.filterForm.get('maxMoq').setValue(null);
    this.filterForm.get('minMoa').setValue(null);
    this.filterForm.get('maxMoa').setValue(null);
    this.filterForm.get('certificationTypes').setValue([]);
    this.filterForm.get('isCpnp').setValue(null);
    this.isCpnp = false;
    this.selected.priceType = this.PRICE_TYPE.ALL;

    if (this.filterType !== this.SEARCH_TYPE.CATEGORY) {
      this.isSelectedAllCategory = true;
      // tslint:disable-next-line: no-unused-expression
      this.allDisplayCategories && this.onUncheckedCategoryAll();
    }

    if (this.filterType !== this.SEARCH_TYPE.BRAND) {
      this.isSelectedAllBrand = true;
      this.onUncheckedBrandAll();
    }
    this.onUncheckedCertificationTypeAll();
  }

  getUmmaPick() {
    if (!this.pagenatedProducts || this.pagenatedProducts.items.length === 0) {
      this.ummaPickService.getUmmaPicks().subscribe((ummaPick) => {
        this.ummaPickProducts = ummaPick;
        this.ummaPickItemCount = this.ummaPickProducts.map((product) => {
          return product.products;
        });
        this.ummaPickItemCount = new Array().concat(...this.ummaPickItemCount);
      });
    }
  }

  // setSelectedBrands(allBrands: Brand[]) {
  //   this.selected.brands = allBrands.map<any>(brand => {
  //     return { ...brand, selected: true };
  //   });
  // }

  getFilteredBrandLabel() {
    if (this.isSelectedAllBrand) {
      return this.t.transform('TERM.ALL');
    } else {
      const selectedBrands = this.selected.brands.filter((brand) => brand.selected);
      if (selectedBrands.length === 0 || selectedBrands.length === this.selected.brands.length) {
        return this.t.transform('TERM.ALL');
      } else if (selectedBrands.length === 1) {
        return selectedBrands[0].englishName;
      } else {
        return this.t.transform('FILTERED_PRODUCT_LIST.FILTERED_LABEL_BRAND', {
          brandName: selectedBrands[0].englishName,
          count: selectedBrands.length - 1,
        });
      }
    }
  }

  getFilteredCerificationLabel() {
    let ret = [this.t.transform('TERM.ALL')];
    this.certificationTypes.filter((certificationType) => {
      if (certificationType.selected) {
        ret = ret.includes('All') ? [] : ret;
        ret.push(certificationType.description);
      }
    });
    if (this.isCpnp) {
      ret = ret.includes('All') ? [] : ret;
      ret.push('CPNP');
    }
    return ret;
  }

  setDisplayCategory(displayCategories: DisplayCategory[]) {
    const allDisplayCategoryTree = this.codeService.getDisplayCategoryAsTree();
    this.selected.displayCategoryTree = allDisplayCategoryTree.createSubTree(displayCategories);
  }

  getFilteredCategoryLabel() {
    if (this.isSelectedAllCategory) {
      return this.t.transform('TERM.ALL');
    } else {
      const mappedNode = this.selected.displayCategoryTree.mappedNode;
      const selectedCategoryIds = Object.keys(mappedNode).filter((id) => {
        return mappedNode[id].selected === true;
      });
      if (
        selectedCategoryIds.length === 0 ||
        selectedCategoryIds.length === this.selected.displayCategoryTree.items.length
      ) {
        return this.t.transform('TERM.ALL');
      } else if (selectedCategoryIds.length === 1) {
        return mappedNode[selectedCategoryIds[0]].item.name;
      } else {
        return this.t.transform('FILTERED_PRODUCT_LIST.FILTERED_LABEL', {
          brandName: mappedNode[selectedCategoryIds[0]].item.name,
          count: selectedCategoryIds.length - 1,
        });
      }
    }
  }

  getFilteredPriceRangeLabel() {
    const filterValue = this.filterForm.value;
    if (this.selected.priceType === this.PRICE_TYPE.SUPPLY_RATE) {
      if (filterValue.minRate && !filterValue.maxRate) {
        return this.t.transform('FILTERED_PRODUCT_LIST.FILTERED_LABEL_SUPPLYRATE_MIN', {
          min: filterValue.minRate,
        });
      } else if (!filterValue.minRate && filterValue.maxRate) {
        return this.t.transform('FILTERED_PRODUCT_LIST.FILTERED_LABEL_SUPPLYRATE_MAX', {
          max: filterValue.maxRate,
        });
      } else if (filterValue.minRate && filterValue.maxRate) {
        return this.t.transform('FILTERED_PRODUCT_LIST.FILTERED_LABEL_SUPPLYRATE', {
          min: filterValue.minRate,
          max: filterValue.maxRate,
        });
      } else {
        return this.t.transform('TERM.SUPPLY_RATE');
      }
    } else if (this.selected.priceType === this.PRICE_TYPE.PRICE) {
      if (filterValue.minPrice && !filterValue.maxPrice) {
        return this.t.transform('FILTERED_PRODUCT_LIST.FILTERED_LABEL_PRICE_MIN', {
          min: filterValue.minPrice,
        });
      } else if (!filterValue.minPrice && filterValue.maxPrice) {
        return this.t.transform('FILTERED_PRODUCT_LIST.FILTERED_LABEL_PRICE_MAX', {
          max: filterValue.maxPrice,
        });
      } else if (filterValue.minPrice && filterValue.maxPrice) {
        return this.t.transform('FILTERED_PRODUCT_LIST.FILTERED_LABEL_PRICE', {
          min: filterValue.minPrice,
          max: filterValue.maxPrice,
        });
      } else {
        return this.t.transform('TERM.PRICE');
      }
    } else {
      return this.t.transform('TERM.ALL');
    }
  }

  getFilteredMoqLabel() {
    const filterValue = this.filterForm.value;

    if (filterValue.minMoq && !filterValue.maxMoq) {
      return this.t.transform('FILTERED_PRODUCT_LIST.FILTERED_LABEL_MOQ_MIN', {
        min: filterValue.minMoq,
      });
    } else if (!filterValue.minMoq && filterValue.maxMoq) {
      return this.t.transform('FILTERED_PRODUCT_LIST.FILTERED_LABEL_MOQ_MAX', {
        max: filterValue.maxMoq,
      });
    } else if (filterValue.minMoq && filterValue.maxMoq) {
      return this.t.transform('FILTERED_PRODUCT_LIST.FILTERED_LABEL_MOQ', {
        min: filterValue.minMoq,
        max: filterValue.maxMoq,
      });
    } else {
      return this.t.transform('TERM.ALL');
    }
  }

  getFilteredMoaLabel() {
    const filterValue = this.filterForm.value;

    if (filterValue.minMoa && !filterValue.maxMoa) {
      return this.t.transform('FILTERED_PRODUCT_LIST.FILTERED_LABEL_MOQ_MIN', {
        min: filterValue.minMoa,
      });
    } else if (!filterValue.minMoa && filterValue.maxMoa) {
      return this.t.transform('FILTERED_PRODUCT_LIST.FILTERED_LABEL_MOQ_MAX', {
        max: filterValue.maxMoa,
      });
    } else if (filterValue.minMoa && filterValue.maxMoa) {
      return this.t.transform('FILTERED_PRODUCT_LIST.FILTERED_LABEL_MOQ', {
        min: filterValue.minMoa,
        max: filterValue.maxMoa,
      });
    } else {
      return this.t.transform('TERM.ALL');
    }
  }

  onShowFilterModal(isShow: boolean) {
    this.isShowOptions = false;
    this.isShowFilterModal = isShow;
  }

  onToggleSection(filterSection: string) {
    this.selectedFilterSection = this.selectedFilterSection === filterSection ? null : filterSection;
  }

  initModalMaximized(dialog: Dialog) {
    setTimeout(() => {
      dialog.maximize();
    }, 0);
  }

  onUncheckedBrandAll() {
    if (this.filterType !== this.SEARCH_TYPE.BRAND) {
      this.selected.brands.forEach((brand) => {
        brand.selected = false;
      });
    }
  }

  onUncheckedCertificationTypeAll() {
    this.certificationTypes.forEach((certificationType) => {
      certificationType.selected = false;
    });
  }

  onUncheckedCategoryAll() {
    // tslint:disable-next-line: no-unused-expression
    this.selected &&
      this.selected.displayCategoryTree &&
      this.selected.displayCategoryTree.items &&
      this.selected.displayCategoryTree.selectAllChildren(this.selected.displayCategoryTree, false);
  }

  onToggleCategory(tree: Tree<DisplayCategory>, $event: any) {
    this.isSelectedAllCategory = false;
    this.selected.displayCategoryTree.selectAll(tree, $event.checked);

    const mappedNode = this.selected.displayCategoryTree.mappedNode;

    const selectedDisplayCategoryIds = Object.keys(mappedNode)
      .filter((id) => mappedNode[id].selected)
      .map((id) => mappedNode[id].item.id);

    if (
      selectedDisplayCategoryIds.length === 0 ||
      selectedDisplayCategoryIds.length === this.selected.displayCategoryTree.items.length
    ) {
      this.isSelectedAllCategory = true;
      $event.source.checked = false;
      this.selected.displayCategoryTree.selectAllChildren(this.selected.displayCategoryTree, false);
    }
    // this.onSubmitFilter();
  }

  isGreaterThen(min: string, max: string, message: string) {
    if (Utils.toNumber(min) > Utils.toNumber(max)) {
      setTimeout(() => {
        const dialog = this._openDialog({
          message,
          close: () => {
            this.filterForm.get('minRate').setValue(null);
            this.filterForm.get('maxRate').setValue(null);
            this.filterForm.get('minPrice').setValue(null);
            this.filterForm.get('maxPrice').setValue(null);
            dialog.close();
          },
        });
      }, 500);
      return true;
    }
    return false;
  }

  initFilter() {
    this.filter.brandIds = [];
    this.filter.displayCategories = [];
    delete this.filter.startSalePrice;
    delete this.filter.endSalePrice;
    delete this.filter.startSupplyRate;
    delete this.filter.endSupplyRate;
    delete this.filter.startMoq;
    delete this.filter.endMoq;
    delete this.filter.startMoa;
    delete this.filter.endMoa;
    delete this.filter.certificationType;
    delete this.filter.isCpnp;
  }

  onChangePriceRange(priceType: string) {
    switch (priceType) {
      case this.PRICE_TYPE.ALL:
        this.filterForm.patchValue({
          minPrice: '',
          maxPrice: '',
          minRate: '',
          maxRate: '',
        });
        break;
      case this.PRICE_TYPE.SUPPLY_RATE:
        this.filterForm.patchValue({
          minPrice: '',
          maxPrice: '',
        });
        break;
      case this.PRICE_TYPE.PRICE:
        this.filterForm.patchValue({
          minRate: '',
          maxRate: '',
        });
        break;
    }

    this.selected.priceType = priceType;
  }

  onSubmitFilter() {
    const formValue = this.filterForm.value;

    if (
      this.selected.priceType === this.PRICE_TYPE.PRICE &&
      this.isGreaterThen(
        formValue.minPrice,
        formValue.maxPrice,
        this.i18n.t('FILTERED_PRODUCT_LIST.LOWEST_PRICE_ERROR')
      )
    ) {
      return;
    } else if (
      this.selected.priceType === this.PRICE_TYPE.SUPPLY_RATE &&
      this.isGreaterThen(formValue.minRate, formValue.maxRate, this.i18n.t('FILTERED_PRODUCT_LIST.LOWEST_RATE_ERROR'))
    ) {
      return;
    }

    if (this.isGreaterThen(formValue.minMoq, formValue.maxMoq, this.i18n.t('FILTERED_PRODUCT_LIST.LOWEST_MOQ_ERROR'))) {
      return;
    }

    if (this.isGreaterThen(formValue.minMoa, formValue.maxMoa, this.i18n.t('FILTERED_PRODUCT_LIST.LOWEST_MOA_ERROR'))) {
      return;
    }

    this.initFilter();

    this.filter.certificationType = this.certificationTypes
      .filter((certificationType) => {
        return certificationType.selected;
      })
      .map((certificationType) => certificationType.code as CERTIFICATION_TYPE);
    this.filter.isCpnp = this.isCpnp;
    if (!this.filter.isCpnp) {
      this.filter.isCpnp = null;
    }

    if (this.filterType === this.SEARCH_TYPE.BRAND) {
      this.filter.brandIds = this.allBrands.map((brand) => brand.id);
    } else if (!this.isSelectedAllBrand) {
      const selectedBrands = this.selected.brands.filter((brand) => brand.selected).map((brand) => brand.id);
      if (selectedBrands.length !== 0 && selectedBrands.length !== this.selected.brands.length) {
        this.filter.brandIds = selectedBrands;
      }
    }

    const mappedNode = this.selected.displayCategoryTree.mappedNode;

    if (this.filterType === this.SEARCH_TYPE.CATEGORY) {
      this.filter.displayCategories = this.filter.displayCategories = this.allDisplayCategories.map(
        (category) => category.code
      );
    } else if (!this.isSelectedAllCategory) {
      const selectedDisplayCategoryIds = Object.keys(mappedNode)
        .filter((id) => mappedNode[id].selected)
        .map((id) => mappedNode[id].item.id);

      if (
        selectedDisplayCategoryIds.length !== 0 &&
        selectedDisplayCategoryIds.length !== this.selected.displayCategoryTree.items.length
      ) {
        this.filter.displayCategories = selectedDisplayCategoryIds
          .filter((id) => mappedNode[id].children.length === 0)
          .map((id) => mappedNode[id].item.code);

        this.filter.displayCategories = this.filter.displayCategories;
      }
    }

    if (this.selected.priceType === this.PRICE_TYPE.PRICE) {
      if (formValue.minPrice) {
        this.filter.startSalePrice = Utils.toNumber(formValue.minPrice);
      }

      if (formValue.maxPrice) {
        this.filter.endSalePrice = Utils.toNumber(formValue.maxPrice);
      }
    } else if (this.selected.priceType === this.PRICE_TYPE.SUPPLY_RATE) {
      if (formValue.minRate) {
        this.filter.startSupplyRate = Utils.toNumber(formValue.minRate);
      }

      if (formValue.maxRate) {
        this.filter.endSupplyRate = Utils.toNumber(formValue.maxRate);
      }
    }

    if (formValue.minMoq) {
      this.filter.startMoq = Utils.toNumber(formValue.minMoq);
    }

    if (formValue.maxMoq) {
      this.filter.endMoq = Utils.toNumber(formValue.maxMoq);
    }

    if (formValue.minMoa) {
      this.filter.startMoa = Utils.toNumber(formValue.minMoa);
    }

    if (formValue.maxMoa) {
      this.filter.endMoa = Utils.toNumber(formValue.maxMoa);
    }

    if (formValue.orderBy) {
      this.filter.setSort(formValue.orderBy.value, formValue.orderBy.orderBy);
    }

    this.onShowFilterModal(false);
    this.checkFiltering();
    this.onChangeFilter.emit(this.filter);
  }
}
